/**
 * Floor Plan Editor Component
 * 
 * Main container for floor plan edit mode with drag/resize capabilities
 * Date: 09-10-2025 12:40:09
 * 
 * @module floor-plan-editor/FloorPlanEditor
 */

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Stage, Layer, Rect, Text as KonvaText } from '../../../lib/konva-optimized';
import { Box, Loader, Center, Text, Stack, Group } from '@mantine/core';
import { EditableRoomNode } from './EditableRoomNode';
import { EditModeToolbar } from './EditModeToolbar';
import { useEditMode } from './useEditMode';
import { useRoomTransform } from './useRoomTransform';
import { useUpdateRoomLayout, useCreateRoomLayout } from '../../../hooks/api/useRoomLayout';
import type { LayoutData, RoomTransformData } from '../../../types/room-layout';
import { detectOverlaps } from './layoutValidation';
import { FloorPlanSidebar } from '../../../components/organisms/FloorPlanSidebar';
import { QuickRoomCreateModal, CustomRoomCreateModal } from '../../../components/modals';
import type { RoomWithDetails } from '../../../api/endpoints/rooms';
import '../../../styles/floor-plan-editor.css';

/**
 * Room data interface
 */
interface FloorPlanRoom {
  id: string;
  number: string;
  name: string;
  capacity: number;
  isActive: boolean;
  position: {
    x: number;
    y: number;
    width: number;
    height: number;
    rotation?: number;
    zIndex?: number;
  };
}

/**
 * Floor plan editor props
 */
interface FloorPlanEditorProps {
  floorId: string;
  rooms: FloorPlanRoom[];
  onExitEditMode: () => void;
  stageWidth?: number;
  stageHeight?: number;
}

/**
 * Floor Plan Editor Component
 * 
 * Provides Edit Mode functionality for arranging room positions on canvas
 * Supports drag, resize, overlap detection, and batch save
 * 
 * @param props - Component props
 * @returns React component
 */
export const FloorPlanEditor: React.FC<FloorPlanEditorProps> = ({
  floorId,
  rooms,
  onExitEditMode,
  stageWidth = 1200,
  stageHeight = 800
}) => {
  // UI state
  const [toolbarCollapsed, setToolbarCollapsed] = useState(false);
  const [canvasWidth, setCanvasWidth] = useState(stageWidth);
  const [canvasHeight, setCanvasHeight] = useState(stageHeight);
  const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });
  const [draggedRoomId, setDraggedRoomId] = useState<string | null>(null);
  const [viewportScale, setViewportScale] = useState(1);
  const [stageOffset, setStageOffset] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Edit mode state management
  const {
    selectedRoomId,
    pendingChanges,
    hasUnsavedChanges,
    updateRoomLayout,
    selectRoom,
    undoAllChanges,
    getPendingChanges,
    exitEditMode
  } = useEditMode(floorId);

  // Track overlapping rooms
  const [overlappingRooms, setOverlappingRooms] = useState<Set<string>>(new Set());

  // Sidebar state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showCustomModal, setShowCustomModal] = useState(false);

  // API mutations
  const updateLayoutMutation = useUpdateRoomLayout();
  const createLayoutMutation = useCreateRoomLayout();

  /**
   * Get current room positions (with pending changes applied)
   */
  const currentRoomPositions = useMemo(() => {
    return rooms.map(room => {
      const pendingChange = pendingChanges.get(room.id);
      if (pendingChange) {
        return {
          roomId: room.id,
          x: pendingChange.x,
          y: pendingChange.y,
          width: pendingChange.width,
          height: pendingChange.height
        };
      }
      return {
        roomId: room.id,
        x: room.position.x,
        y: room.position.y,
        width: room.position.width,
        height: room.position.height
      };
    });
  }, [rooms, pendingChanges]);

  // Calculate optimal viewport scale to fit all rooms
  const calculateViewportScale = useCallback(() => {
    if (rooms.length === 0) {
      setViewportScale(1);
      setStageOffset({ x: 0, y: 0 });
      return;
    }

    // Calculate bounds of all rooms (including pending changes)
    const bounds = rooms.reduce((acc, room) => {
      const pendingChange = pendingChanges.get(room.id);
      const position = pendingChange || room.position;

      return {
        minX: Math.min(acc.minX, position.x),
        maxX: Math.max(acc.maxX, position.x + position.width),
        minY: Math.min(acc.minY, position.y),
        maxY: Math.max(acc.maxY, position.y + position.height)
      };
    }, { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity });

    const contentWidth = bounds.maxX - bounds.minX;
    const contentHeight = bounds.maxY - bounds.minY;

    // Add padding (10% on each side)
    const padding = 100;
    const paddedWidth = contentWidth + (padding * 2);
    const paddedHeight = contentHeight + (padding * 2);

    // Calculate scale to fit content in viewport
    const scaleX = canvasWidth / paddedWidth;
    const scaleY = canvasHeight / paddedHeight;

    const optimalScale = Math.min(scaleX, scaleY, 1); // Don't scale up beyond 1:1

    // Calculate center offset
    const centerX = (bounds.minX + bounds.maxX) / 2;
    const centerY = (bounds.minY + bounds.maxY) / 2;

    const offsetX = (canvasWidth / 2) - (centerX * optimalScale);
    const offsetY = (canvasHeight / 2) - (centerY * optimalScale);

    setViewportScale(optimalScale);
    setStageOffset({ x: offsetX, y: offsetY });
  }, [rooms, pendingChanges, canvasWidth, canvasHeight]);

  // Recalculate viewport when rooms or dimensions change
  useEffect(() => {
    calculateViewportScale();
  }, [calculateViewportScale]);

  /**
   * Room transform handlers
   * Uses dynamic canvas dimensions for accurate drag boundaries
   * @created 17-10-2025 07:13:14
   */
  const { handleDragEnd, handleTransformEnd, createDragBoundFunc } = useRoomTransform({
    rooms: currentRoomPositions,
    stageWidth: canvasWidth,
    stageHeight: canvasHeight,
    gridSize: 20,
    enableSnap: true,
    onTransform: (data: RoomTransformData) => {
      // Get current room data
      const room = rooms.find(r => r.id === data.roomId);
      if (!room) return;

      // Get pending change or current position
      const current = pendingChanges.get(data.roomId) || {
        roomId: data.roomId,
        floorId,
        x: room.position.x,
        y: room.position.y,
        width: room.position.width,
        height: room.position.height,
        rotation: room.position.rotation || 0,
        zIndex: room.position.zIndex || 0
      };

      // Update with new transform data (ROUND to integers to avoid floating-point issues)
      const updated: LayoutData = {
        ...current,
        ...(data.x !== undefined && { x: Math.round(data.x) }),
        ...(data.y !== undefined && { y: Math.round(data.y) }),
        ...(data.width !== undefined && { width: Math.round(data.width) }),
        ...(data.height !== undefined && { height: Math.round(data.height) }),
        ...(data.rotation !== undefined && { rotation: Math.round(data.rotation) })
      };

      updateRoomLayout(data.roomId, updated);
    },
    onOverlapDetected: (roomIds: string[]) => {
      setOverlappingRooms(new Set(roomIds));
    }
  });

  /**
   * Check overlaps for all rooms with pending changes
   */
  useEffect(() => {
    const allOverlaps = new Set<string>();

    for (const [roomId, layout] of pendingChanges.entries()) {
      const overlap = detectOverlaps(
        { x: layout.x, y: layout.y, width: layout.width, height: layout.height, roomId },
        currentRoomPositions,
        roomId
      );

      if (overlap.hasOverlap) {
        allOverlaps.add(roomId);
        overlap.overlappingRoomIds.forEach(id => allOverlaps.add(id));
      }
    }

    setOverlappingRooms(allOverlaps);
  }, [pendingChanges, currentRoomPositions]);

  /**
   * Handle save layout action
   */
  const handleSave = useCallback(async () => {
    const changes = getPendingChanges();
    if (changes.length === 0) return;

    try {
      // Batch update
      await updateLayoutMutation.mutateAsync({
        floorId,
        layouts: changes
      });

      // Exit edit mode on success
      const exited = exitEditMode();
      if (exited) {
        onExitEditMode();
      }
    } catch (error) {
      console.error('Failed to save layout:', error);
    }
  }, [floorId, getPendingChanges, updateLayoutMutation, exitEditMode, onExitEditMode]);

  /**
   * Handle cancel action
   */
  const handleCancel = useCallback(() => {
    const exited = exitEditMode();
    if (exited) {
      onExitEditMode();
    }
  }, [exitEditMode, onExitEditMode]);

  /**
   * Handle undo all changes
   */
  const handleUndo = useCallback(() => {
    undoAllChanges();
    setOverlappingRooms(new Set());
  }, [undoAllChanges]);



  /**
   * Handle create from template
   */
  const handleCreateFromTemplate = useCallback(() => {
    setShowTemplateModal(true);
  }, []);

  /**
   * Handle create custom room
   */
  const handleCreateCustom = useCallback(() => {
    setShowCustomModal(true);
  }, []);

  /**
   * Handle keyboard shortcuts
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape key: deselect or exit
      if (e.key === 'Escape') {
        if (selectedRoomId) {
          selectRoom(null);
        } else {
          handleCancel();
        }
      }

      // Ctrl/Cmd + S: Save
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (hasUnsavedChanges && overlappingRooms.size === 0) {
          handleSave();
        }
      }

      // Ctrl/Cmd + Z: Undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        handleUndo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedRoomId, hasUnsavedChanges, overlappingRooms, selectRoom, handleCancel, handleSave, handleUndo]);

  /**
   * Canvas dimensions using direct container measurements
   * Uses flex layout instead of ResizeObserver for better performance
   * @created 17-10-2025 07:02:58
   */
  useEffect(() => {
    const updateCanvasDimensions = () => {
      if (!containerRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;
      const containerHeight = containerRef.current.offsetHeight;

      // Use full container dimensions (flex layout handles sidebar/toolbar spacing)
      setCanvasWidth(containerWidth);
      setCanvasHeight(containerHeight);
      setContainerDimensions({ width: containerWidth, height: containerHeight });
    };

    // Initial calculation
    updateCanvasDimensions();

    // Listen for window resize (for fullscreen changes)
    window.addEventListener('resize', updateCanvasDimensions);
    
    return () => window.removeEventListener('resize', updateCanvasDimensions);
  }, [sidebarCollapsed, toolbarCollapsed]);

  if (!floorId) {
    return (
      <Center h={600}>
        <Text c="dimmed">No floor selected</Text>
      </Center>
    );
  }

  // Always show the editor interface, even with no rooms
  // The sidebar allows creating new rooms
  return (
    <Box 
      className="floor-plan-editor-container"
      style={{ position: 'relative', width: '100%', height: '100vh', display: 'flex' }}
    >
      <Group gap={0} align="stretch" style={{ height: '100%', width: '100%' }}>
        {/* Sidebar - Always visible in Edit Mode */}
        <FloorPlanSidebar
          floorId={floorId}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          onCreateFromTemplate={handleCreateFromTemplate}
          onCreateCustom={handleCreateCustom}
        />

        {/* Main Content Area - Takes remaining space */}
        <Box ref={containerRef} style={{ 
          flex: 1, 
          position: 'relative', 
          minWidth: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Edit Mode Toolbar */}
          <EditModeToolbar
            collapsed={toolbarCollapsed}
            onToggleCollapse={() => setToolbarCollapsed(!toolbarCollapsed)}
            hasUnsavedChanges={hasUnsavedChanges}
            isSaving={updateLayoutMutation.isPending}
            overlapCount={overlappingRooms.size}
            onSave={handleSave}
            onCancel={handleCancel}
            onUndo={handleUndo}
            disabled={false}
            modalOpen={showTemplateModal || showCustomModal}
          />

          {/* Konva Stage */}
          <Stage
            width={canvasWidth}
            height={canvasHeight}
            scaleX={viewportScale}
            scaleY={viewportScale}
            x={stageOffset.x}
            y={stageOffset.y}
            style={{
              border: '3px solid #2563EB',
              borderRadius: '8px',
              backgroundColor: '#F8FAFC',
              width: '100%',
              height: '100%',
              maxWidth: '100%'
            }}
          >
            {/* Background Layer - Non-interactive */}
            <Layer listening={false}>
              {/* Empty canvas message if no rooms */}
              {rooms.length === 0 && (
                <>
                  <Rect
                    x={50}
                    y={250}
                    width={sidebarCollapsed ? canvasWidth - 100 : canvasWidth - 420}
                    height={150}
                    fill="#f8f9fa"
                    stroke="#dee2e6"
                    strokeWidth={2}
                    dash={[10, 5]}
                    cornerRadius={8}
                  />
                  <KonvaText
                    x={50}
                    y={280}
                    width={sidebarCollapsed ? canvasWidth - 100 : canvasWidth - 420}
                    text="No rooms on this floor yet"
                    fontSize={20}
                    fontStyle="bold"
                    fill="#868e96"
                    align="center"
                  />
                  <KonvaText
                    x={50}
                    y={310}
                    width={sidebarCollapsed ? canvasWidth - 100 : canvasWidth - 420}
                    text="Use the sidebar to create rooms or drag existing ones onto the canvas"
                    fontSize={14}
                    fill="#adb5bd"
                    align="center"
                  />
                </>
              )}
            </Layer>

            {/* Single Layer - All Rooms (keep nodes stable during drag) */}
            <Layer>
              {rooms.map((room) => {
                const pendingChange = pendingChanges.get(room.id);
                const currentPosition = pendingChange || room.position;

                return (
                  <EditableRoomNode
                    key={room.id}
                    room={{
                      id: room.id,
                      number: room.number,
                      name: room.name,
                      position: {
                        x: currentPosition.x,
                        y: currentPosition.y,
                        width: currentPosition.width,
                        height: currentPosition.height
                      }
                    }}
                    isEditMode={true}
                    isSelected={selectedRoomId === room.id}
                    hasOverlap={overlappingRooms.has(room.id)}
                    onSelect={selectRoom}
                    onDragStart={() => setDraggedRoomId(room.id)}
                    onDragEnd={(roomId, e) => {
                      setDraggedRoomId(null);
                      handleDragEnd(roomId, e);
                    }}
                    onTransformEnd={handleTransformEnd}
                  />
                );
              })}
            </Layer>
      </Stage>

          {/* Loading Overlay */}
          {updateLayoutMutation.isPending && (
            <Box
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 200
              }}
            >
              <Stack align="center" gap="sm">
                <Loader size="lg" color="blue" />
                <Text size="sm" fw={500}>Saving layout...</Text>
              </Stack>
            </Box>
          )}
        </Box>
      </Group>

      {/* Template Modal */}
      {showTemplateModal && (
        <QuickRoomCreateModal
          floorId={floorId}
          opened={showTemplateModal}
          onClose={() => setShowTemplateModal(false)}
        />
      )}

      {/* Custom Room Modal */}
      {showCustomModal && (
        <CustomRoomCreateModal
          floorId={floorId}
          opened={showCustomModal}
          onClose={() => setShowCustomModal(false)}
        />
      )}
    </Box>
  );
};

